/**
 * Utility functions for parsing markdown content from compliance rules
 */

/**
 * Extracts the "Rule" section from markdown content.
 * The Rule section starts with **Rule** and continues until the next bold heading
 * (like **Applies when**, **What good looks like**, **Why it matters**) or end of string.
 *
 * @param markdown - The full markdown content
 * @returns The extracted Rule section including the heading, or empty string if not found
 */
export function extractRuleSection(markdown: string): string {
  if (!markdown || !markdown.trim()) {
    return '';
  }

  // Pattern to match **Rule** heading (case-insensitive, handles variations)
  // Also matches variations like "**Rule:**" or "**Rule**" with optional colon
  const ruleHeadingPattern = /\*\*Rule\*\*:?\s*\n?/i;
  
  // Pattern to match next bold heading sections
  const nextSectionPatterns = [
    /\*\*Applies when\*\*:?\s*\n?/i,
    /\*\*What good looks like\*\*:?\s*\n?/i,
    /\*\*Why it matters\*\*:?\s*\n?/i,
    /\*\*[^*]+\*\*:?\s*\n?/g, // Any other bold heading as fallback
  ];

  // Find the Rule heading
  const ruleMatch = markdown.match(ruleHeadingPattern);
  if (!ruleMatch) {
    // If no Rule heading found, return empty string
    return '';
  }

  const ruleStartIndex = ruleMatch.index! + ruleMatch[0].length;
  
  // Find the start of the next section
  let nextSectionIndex = markdown.length;
  
  for (const pattern of nextSectionPatterns) {
    // Reset regex lastIndex for global patterns
    if (pattern.global) {
      pattern.lastIndex = 0;
    }
    
    const match = markdown.substring(ruleStartIndex).match(pattern);
    if (match && match.index !== undefined) {
      const absoluteIndex = ruleStartIndex + match.index;
      if (absoluteIndex < nextSectionIndex) {
        nextSectionIndex = absoluteIndex;
      }
    }
  }

  // Extract the Rule section (including the heading)
  const ruleSection = markdown.substring(ruleMatch.index!, nextSectionIndex).trim();
  
  return ruleSection || '';
}

